import { memo } from 'react';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { dateFormat, numberToCurrency, numberToPercent } from 'utils/translate';
import { Offer as OfferType } from 'hooks/webapi/application/useFirstLoanOfferQuery';
import { ClientOffer } from 'hooks/webapi/client/useClientOfferQuery';
import styles from './Offer.module.scss';
import Placeholder from '../Placeholder/Placeholder';

export type Props = {
  dataTest?: string;
  offer?: OfferType | ClientOffer;
  loading?: boolean;
  className?: string;
  showInterest?: boolean;
};

const Offer = memo(function Offer({
  offer,
  showInterest = true,
  loading = false,
  className,
  dataTest = 'offer',
}: Props) {
  const { t } = useTranslation(['upsale', 'calculator']);
  return (
    <section className={classes(styles['offer-wrapper'], className)}>
      <hr className={styles.hr} />
      <div className={styles.row} data-test={`${dataTest}-newPrincipal-row`}>
        <div data-test={`${dataTest}-newPrincipal-label`}>
          {t('table.loan')}
        </div>
        <div data-test="offer-new-principal">
          <Placeholder
            loading={loading}
            dataTest={`${dataTest}-newPrincipal-value`}
          >
            {numberToCurrency(offer?.newPrincipal)}
          </Placeholder>
        </div>
      </div>
      <hr className={styles.hr} />
      <div className={styles.row} data-test={`${dataTest}-fee-row`}>
        <div data-test={`${dataTest}-fee-label`}>
          {t('calculator:offer.mtp')}
        </div>
        <div>
          <Placeholder dataTest={`${dataTest}-fee-value`} loading={loading}>
            {numberToCurrency(offer?.loanExtensionFees[0]?.fee)}
          </Placeholder>
        </div>
      </div>
      <hr className={styles.hr} />
      <div
        className={styles.row}
        data-test={`${dataTest}-offer-table-future-extension-fees-row`}
      >
        <div
          className={styles.lineHeightRow}
          data-test={`${dataTest}-offer-table-future-extension-fee-label`}
        >
          {t('table.futureExtensionFees')}
        </div>
        <div
          className={styles.lineHeightRowDate}
          data-test={`${dataTest}-offer-table-future-extension-fee-value`}
        >
          <Placeholder
            dataTest="offer-table-future-extension-fees-value"
            loading={loading}
          >
            {numberToCurrency(offer?.futureExtensionFees[0].fee)}
          </Placeholder>
        </div>
      </div>
      <hr className={styles.hr} />
      <div className={styles.row} data-test={`${dataTest}-dueDate-row`}>
        <div
          className={styles.lineHeightRow}
          data-test={`${dataTest}-dueDate-label`}
        >
          {t('table.due_date')}
        </div>
        <div
          className={styles.lineHeightRowDate}
          data-test={`${dataTest}-dueDate-value`}
        >
          <Placeholder dataTest="offer-agreement-end-date" loading={loading}>
            {dateFormat(offer?.agreementEndDate)}
          </Placeholder>
        </div>
      </div>
      <hr className={styles.hr} />
      {showInterest && (
        <div className={styles.row} data-test={`${dataTest}-interest-row`}>
          <div data-test={`${dataTest}-interest-label`}>
            {t('table.interest')}
          </div>
          <div>
            <Placeholder
              dataTest="offer-agreement-interest-rate"
              loading={loading}
            >
              {numberToPercent(offer?.interestRate)}
            </Placeholder>
          </div>
        </div>
      )}
      <div
        className={styles.info}
        data-test="loan-example"
        dangerouslySetInnerHTML={{
          __html: import.meta.env.VITE_CALCULATOR_HTML_TEXT,
        }}
      />
    </section>
  );
});

export default Offer;
